import type { ReactNode } from 'react';

/**
 * The Explore icon set.
 *
 * The public header already speaks one icon language — a 24-box, a single
 * stroke, currentColor, 1.8 wide, round caps — and everything below it was
 * speaking a different one: '⌂', '⚒', '⊞', typographic glyphs that render in
 * whatever the reader's font decides, at whatever weight it decides, next to
 * hairline SVGs drawn to a grid. On a phone the difference reads as a page
 * half-finished.
 *
 * So the sections borrow the header's language rather than inventing a third.
 * What they add is COLOUR, and only where colour carries meaning: each of the
 * three Explore sections owns one hue, used as a tinted tile behind its glyph,
 * so the same hue means Homes wherever it appears — a tab, a posting card, an
 * empty state. Never more than one hue in a component, and never a hue on
 * something that is not one of the three.
 *
 * The tints are defined as tokens in public.css so dark mode redefines them
 * once, instead of every icon carrying a hard-coded colour that survives the
 * theme switch.
 */

const svg = (children: ReactNode, size = 18) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>{children}</svg>
);

/** A roof over a magnifier: looking for somewhere to live, not a house. */
export const IconHomes = (size?: number) => svg(
  <><circle cx="11" cy="11" r="7" /><path d="M16.2 16.2 21 21" />
    <path d="M8.4 11.3 11 9.2l2.6 2.1" /><path d="M9.3 10.6v3.2h3.4v-3.2" /></>, size);

/** Spanner and screwdriver crossed: a trade, not a single tool. */
export const IconServices = (size?: number) => svg(
  <><path d="M14.7 6.3a3.6 3.6 0 0 0 4.6 4.6l-7.7 7.7a2 2 0 0 1-2.8-2.8z" />
    <path d="M6.4 4.3 4.3 6.4l2.6 2.6 1.5-1.5z" />
    <path d="M8.4 7.5 12 11.1" /></>, size);

/** A price tag: something for sale, held by one hand. */
export const IconMarket = (size?: number) => svg(
  <><path d="M3.6 11.4V4.6a1 1 0 0 1 1-1h6.8a1 1 0 0 1 .7.3l8 8a1 1 0 0 1 0 1.4l-6.8 6.8a1 1 0 0 1-1.4 0l-8-8a1 1 0 0 1-.3-.7z" />
    <circle cx="8" cy="8" r="1.5" /></>, size);

/** A square with a plus: put something in. */
export const IconPost = (size?: number) => svg(
  <><rect x="3.5" y="3.5" width="17" height="17" rx="4" />
    <path d="M12 8.5v7" /><path d="M8.5 12h7" /></>, size);

/** A door with an arrow going in: the way into a listing you own. */
export const IconMine = (size?: number) => svg(
  <><path d="M4 4.5h8.5v15H4z" /><path d="M15 12h5.5" /><path d="M18 9.2 20.8 12 18 14.8" /></>, size);

/** A house with a price tag: a property that is for sale, not to let. */
export const IconBuy = (size?: number) => svg(
  <><path d="M3.5 10.5 12 4l8.5 6.5" />
    <path d="M5.5 9.6V19a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1V9.6" />
    <path d="M12 12.5h3.2v3.2L12 18.9l-3.2-3.2z" />
    <path d="M14 14.4h.01" /></>, size);

/** The sections, each owning one hue. */
export type Section = 'homes' | 'buy' | 'services' | 'market' | 'post';

export const SECTION_ICON: Record<Section, (size?: number) => ReactNode> = {
  homes: IconHomes, buy: IconBuy, services: IconServices, market: IconMarket, post: IconPost,
};

/**
 * A glyph on its section's tinted tile.
 *
 * `flat` drops the tile for places that are already dense — a tab strip, an
 * inline label — where a coloured square on every item would be noise rather
 * than a signal.
 */
export function SectionIcon(
  { section, size = 18, flat = false }: { section: Section; size?: number; flat?: boolean },
) {
  const glyph = SECTION_ICON[section](size);
  if (flat) return <span className={`sect-glyph sect-${section}`}>{glyph}</span>;
  return <span className={`sect-tile sect-${section}`} aria-hidden>{glyph}</span>;
}
